import Typography from "@mui/material/Typography"
import { observer } from "mobx-react-lite/dist"
import { useProposalsEditPaymentStore } from "../ProposalsEditPaymentStore"
import { ServiceRateTypeEnum } from "@gap/backend/app-services/services/models/ServiceRateTypesDto"
import Stack from '@mui/material/Stack';
import { useTheme } from "theme/useTheme"
import TextField from "@mui/material/TextField";
import ToggleButtonGroupCustom from "reusable/mui/toggle-button-group/ToggleButtonGroupCustom";
import InputAdornment from "@mui/material/InputAdornment";
import useStore from "reusable/mobx-store-utils";
import AutocompleteSingleNonClearable from "reusable/mui/auto-complete/AutocompleteSingleNonClearable";
import ProposalsEditPaymentServiceBlockStore, { ProposalsEditPaymentServiceBlockStoreProvider, useProposalsEditPaymentServiceBlockStore } from "./ProposalsEditPaymentServiceBlockStore";
import { proposalStep3ServiceAgreementTypeEnum } from "@gap/backend/app-services/proposal/models/ProposalStep3Dto";
import { notNull } from "@gap/utils/utils";
import { useOrgGuardStore } from "features/main/main-router/org-guard/OrgGuardStore";

export default observer(function ProposalsEditPaymentServiceBlock({ step3ServiceId, rateType }: { step3ServiceId: string, rateType: ServiceRateTypeEnum }) {
   const theme = useTheme()
   const proposalEditPaymentStore = useProposalsEditPaymentStore()
   const proposalsEditPaymentServiceBlockStore = useStore(sp => new ProposalsEditPaymentServiceBlockStore(sp), {
      step3ServiceId, rateType, proposalEditPaymentStore
   })

   return <ProposalsEditPaymentServiceBlockStoreProvider store={proposalsEditPaymentServiceBlockStore}>
      <Stack sx={{
         gap: 1,
         flexDirection: { md: 'row' },
         alignItems: { md: 'end' },
      }}>
         <Typography variant="body1" sx={{ flexShrink: 0 }}>{proposalsEditPaymentServiceBlockStore.step2Service.serviceName}</Typography>
         <Stack sx={{
            borderBottom: `1px dashed ${theme.palette.gap.primary['200']}`,
            flexGrow: 1,
            display: { xs: 'none', md: 'flex' }
         }} />
         <Stack sx={{
            flexDirection: 'row',
            gap: 1,
            justifyContent: { md: 'stretch' },
         }}>
            <ServicePrice />
            <ServiceRateTypeSelector />
            {proposalsEditPaymentServiceBlockStore.showServiceUnits &&
               <ServiceUnitSelector />}
            <ServiceEstimateSelector />
         </Stack>
      </Stack>
   </ProposalsEditPaymentServiceBlockStoreProvider>
})

const ServicePrice = observer(function ServicePrice() {
   const proposalsEditPaymentServiceBlockStore = useProposalsEditPaymentServiceBlockStore()
   const orgGuardStore = useOrgGuardStore()
   const currencySymbol = notNull(orgGuardStore.org).currencySymbol

   return proposalsEditPaymentServiceBlockStore.formHandler.servicePrice.bindInput(ip => <TextField
      variant='outlined'
      slotProps={{
         input: {
            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
         }
      }}
      {...ip}
      size="small"
      sx={{ width: '134px' }}
   />)
})

const ServiceRateTypeSelector = observer(function ServiceRateTypeSelector() {
   const proposalsEditPaymentServiceBlockStore = useProposalsEditPaymentServiceBlockStore()

   return <AutocompleteSingleNonClearable
      options={proposalsEditPaymentServiceBlockStore.serviceRateOptions}
      value={proposalsEditPaymentServiceBlockStore.fields.startingPriceRate}
      onChange={proposalsEditPaymentServiceBlockStore.onNewRateType}
      size="small"
      sx={{ display: 'inline-flex', width: '217px' }}
   />
})

const ServiceUnitSelector = observer(function ServiceUnitSelector() {
   const proposalsEditPaymentServiceBlockStore = useProposalsEditPaymentServiceBlockStore()

   return proposalsEditPaymentServiceBlockStore.formHandler.serviceUnits.bindInput(ip =>
      <TextField
         fullWidth
         label="#"
         size="small"
         sx={{ width: '48px' }}
         {...ip}
      />
   )
})

const ServiceEstimateSelector = observer(function ServiceEstimateSelector() {
   const proposalsEditPaymentServiceBlockStore = useProposalsEditPaymentServiceBlockStore()

   return <ToggleButtonGroupCustom
      value={proposalsEditPaymentServiceBlockStore.fields.serviceAgreementType}
      onChange={proposalsEditPaymentServiceBlockStore.onNewPriceType}
      size="small"
      options={[...proposalStep3ServiceAgreementTypeEnum]}
      exclusive
      sx={{ height: '40px' }}
      buttonsxprops={{ textTransform: 'none', borderRadius: '8px' }}
   />
})
